/* ============================================================================
   DEV DASHBOARD (réservé au plan « admin »)
   ----------------------------------------------------------------------------
   - Statistiques d'utilisation : une seule RPC `admin_dashboard()` (SECURITY
     DEFINER, verrouillée par is_admin() côté Postgres) renvoie tout en jsonb.
   - Gestion des publicités : CRUD sur la table `ads` (RLS admin) + téléversement
     des visuels dans le bucket Storage public « ads ».
   La protection est SERVEUR (RLS + RPC) : masquer le lien dans le header n'est
   qu'un confort d'interface.
   ========================================================================== */
import { supabase } from "./supabaseClient.js";
import { renderToCanvas } from "./render.js";

const $ = (id) => document.getElementById(id);
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const fmtDay = (iso) => new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" }).format(new Date(iso));

let onAdsChanged = null;        // callback de main.js : recharge les carrousels
let sportLabel = (s) => s;      // libellés de sports fournis par main.js
let bound = false;              // les listeners du formulaire ne se posent qu'une fois
let openForReview = null;       // callback de main.js : ouvre une proposition dans l'éditeur
let onModerated = null;         // callback de main.js : met à jour le badge « en attente »

export async function renderDashboard(opts = {}) {
  onAdsChanged = opts.onAdsChanged || onAdsChanged;
  sportLabel = opts.sportLabel || sportLabel;
  openForReview = opts.openSchema || openForReview;
  onModerated = opts.onModerated || onModerated;

  $("dashUpdated").textContent = "Chargement…";
  const { data, error } = await supabase.rpc("admin_dashboard");
  if (error) {
    $("dashUpdated").textContent = "Accès refusé ou erreur : " + (error.message || error);
    return;
  }
  const d = data || {};
  $("dashUpdated").textContent =
    `Période ${d.period} — actualisé à ${new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(new Date())}`;

  renderKpis(d);
  renderChart(d.by_day || []);
  renderBars($("dashSports"), (d.by_sport || []).map((r) => [sportLabel(r.sport), r.n]));
  renderBars($("dashKinds"), (d.by_kind || []).map((r) => [r.kind === "exercise" ? "Exercice" : r.kind === "match" ? "Match" : r.kind, r.n]));
  renderUsers(d.users || []);
  await renderFeedbackAdmin();
  await renderCommunityStats();
  await renderCommunityModeration();
  await renderAdsAdmin();
  bindAdsForm();
}


/* ------------------ Retours bêta (👍/👎) --------------------------------
   Lecture directe de la table `feedback` (RLS admin). KPIs 👍/👎 +
   satisfaction, liste des derniers 👎 (prompt, commentaire, route, retente),
   et EXPORT des 👎 au format des lots de bancs (ai-runs-prod-AAAAMMJJ.json) :
   le fichier téléchargé se rejoue tel quel avec eval:fidelity/eval:legibility
   et sert de base à une « salve retours prod ». */
let fbRows = [];
async function renderFeedbackAdmin() {
  const kpis = $("dashFbKpis"), table = $("dashFbTable");
  if (!kpis || !table) return;
  const { data, error } = await supabase.from("feedback")
    .select("id,created_at,verdict,comment,prompt,sport,kind,route,corrected,famille,analyse,scene,issues")
    .order("created_at", { ascending: false }).limit(500);
  if (error) { kpis.innerHTML = `<p class="dash-empty">Retours indisponibles : ${esc(error.message || error)}</p>`; return; }
  fbRows = data || [];
  const up = fbRows.filter((r) => r.verdict === "up").length;
  const down = fbRows.length - up;
  const sat = fbRows.length ? Math.round((up / fbRows.length) * 100) : null;
  const seven = fbRows.filter((r) => Date.now() - new Date(r.created_at).getTime() < 7 * 864e5).length;
  // Repère « dernier export » (localStorage) : combien de 👎 sont arrivés
  // DEPUIS. C'est un simple CONFORT d'export incrémental — le registre réel des
  // cas déjà traités reste les fixtures du dépôt (rien n'est perdu si ce repère
  // saute : il suffit de tout ré-exporter, les déjà-traités sont ignorés à la
  // réception).
  const lastExport = localStorage.getItem(FB_LAST_EXPORT_KEY);
  const isNew = (r) => !lastExport || r.created_at > lastExport;
  const nbNew = fbRows.filter((r) => r.verdict === "down" && isNew(r)).length;
  const kpi = (label, value, hint = "") =>
    `<div class="kpi"><span class="kpi-value">${esc(value)}</span><span class="kpi-label">${label}</span>${hint ? `<span class="kpi-hint">${hint}</span>` : ""}</div>`;
  kpis.innerHTML =
    kpi("👍 Fidèles", up) +
    kpi("👎 Signalés", down) +
    kpi("Nouveaux 👎", nbNew, lastExport ? "depuis le dernier export" : "aucun export encore") +
    kpi("Satisfaction", sat == null ? "—" : sat + " %");
  const downs = fbRows.filter((r) => r.verdict === "down").slice(0, 30);
  table.innerHTML = downs.length
    ? "<tr><th>Date</th><th>Sport</th><th>Route</th><th>Prompt</th><th>Commentaire</th></tr>" + downs.map((r) =>
      `<tr${isNew(r) ? ' class="fb-new"' : ""}><td>${esc(fmtDay(r.created_at))}${isNew(r) ? " •" : ""}</td><td>${esc(r.sport || "?")}</td><td>${esc(r.route || "?")}${r.corrected ? " ↻" : ""}</td>` +
      `<td title="${esc(r.prompt)}">${esc(String(r.prompt || "").slice(0, 70))}${(r.prompt || "").length > 70 ? "…" : ""}</td>` +
      `<td>${esc(r.comment || "—")}</td></tr>`).join("")
    : `<tr><td class="dash-empty">Aucun 👎 pour l'instant — c'est bon signe.</td></tr>`;
  const bindOnce = (id, fn) => { const b = $(id); if (b && !b.dataset.bound) { b.dataset.bound = "1"; b.addEventListener("click", fn); } };
  bindOnce("dashFbExportNew", () => exportFeedbackLot(true));
  bindOnce("dashFbExportAll", () => exportFeedbackLot(false));
}
/* Télécharge les 👎 au format des lots de salves (rejouable par les bancs).
   onlyNew=true : seulement les retours arrivés depuis le dernier export
   (repère localStorage) ; sinon tous. Un export met à jour le repère. */
const FB_LAST_EXPORT_KEY = "tactiq_fb_last_export";
function exportFeedbackLot(onlyNew) {
  const lastExport = localStorage.getItem(FB_LAST_EXPORT_KEY);
  let downs = fbRows.filter((r) => r.verdict === "down");
  if (onlyNew && lastExport) downs = downs.filter((r) => r.created_at > lastExport);
  downs = downs.slice().reverse();   // chronologique (le plus ancien d'abord)
  if (!downs.length) { alert(onlyNew ? "Aucun nouveau 👎 depuis le dernier export." : "Aucun 👎 à exporter."); return; }
  // Repère mis à jour au created_at le PLUS RÉCENT exporté.
  const maxCreated = downs.reduce((m, r) => (r.created_at > m ? r.created_at : m), downs[0].created_at);
  const lot = downs.map((r, i) => ({
    id: `PROD${i + 1} 👎${r.comment ? " — " + String(r.comment).slice(0, 60) : ""}`,
    prompt: r.prompt,
    sport: r.sport || undefined,
    kind: r.kind || "exercise",
    route: r.route || null,
    corrected: !!r.corrected,
    issues: Array.isArray(r.issues) ? r.issues : [],
    analyse: r.analyse ?? null,
    scene: r.scene ?? null,
    comment: r.comment || null,
    created_at: r.created_at,
  }));
  const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const blob = new Blob([JSON.stringify(lot, null, 1)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `ai-runs-prod-${stamp}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
  // Avance le repère « dernier export » et rafraîchit les compteurs (les
  // exportés ne sont plus « nouveaux »).
  if (!lastExport || maxCreated > lastExport) localStorage.setItem(FB_LAST_EXPORT_KEY, maxCreated);
  renderFeedbackAdmin();
}

/* ------------------ Bibliothèque commune : statistiques -------------------
   L'admin lit TOUTES les lignes (RLS `community_read` via is_admin()). On ne
   récupère que des colonnes légères (pas le jsonb `data`) et on agrège côté
   client : volumes par statut + activité, niveau et type les plus partagés,
   contributeur le plus actif. */
const KIND_LABEL = { exercise: "Exercice", match: "Match" };
async function renderCommunityStats() {
  const kpis = $("dashCommunityKpis");
  const facts = $("dashCommunityFacts");
  if (!kpis || !facts) return;
  const { data, error } = await supabase
    .from("community_schemas")
    .select("status,sport,kind,niveau,author_name")
    .limit(5000);
  if (error) {
    kpis.innerHTML = "";
    facts.innerHTML = `<p class="dash-help">Statistiques indisponibles : ${esc(error.message)}</p>`;
    return;
  }
  const rows = data || [];
  const total = rows.length;
  const approved = rows.filter((r) => r.status === "approved").length;
  const rejected = rows.filter((r) => r.status === "rejected").length;
  const pending = rows.filter((r) => r.status === "pending").length;
  const decided = approved + rejected;
  const rate = decided ? Math.round((approved / decided) * 100) : null;

  const kpi = (label, value, hint = "") =>
    `<div class="kpi"><span class="kpi-value">${esc(value)}</span><span class="kpi-label">${esc(label)}</span>${hint ? `<span class="kpi-hint">${esc(hint)}</span>` : ""}</div>`;
  kpis.innerHTML =
    kpi("Demandes de partage", total) +
    kpi("Publiées", approved, rate != null ? `${rate}% des décidées` : "") +
    kpi("Écartées", rejected) +
    kpi("En attente", pending);

  if (!total) { facts.innerHTML = `<p class="dash-help">Aucune proposition pour l'instant.</p>`; return; }

  // Le « plus partagé » se lit sur les propositions PUBLIÉES (ce qui vit
  // réellement dans la bibliothèque) ; à défaut, sur l'ensemble des demandes.
  const base = approved ? rows.filter((r) => r.status === "approved") : rows;
  const scope = approved ? "publiées" : "demandes";

  // Mode (valeur la plus fréquente) d'une colonne, avec son décompte.
  const topOf = (key, fmt = (v) => v) => {
    const counts = new Map();
    base.forEach((r) => {
      const v = r[key];
      if (v == null || v === "") return;
      counts.set(v, (counts.get(v) || 0) + 1);
    });
    let bestK = null, bestN = 0;
    counts.forEach((n, k) => { if (n > bestN) { bestN = n; bestK = k; } });
    return bestK == null ? null : { label: fmt(bestK), n: bestN };
  };
  const topSport = topOf("sport", (s) => sportLabel(s));
  const topNiveau = topOf("niveau", (n) => `Niveau ${n} — ${NIVEAU_LABEL[n] || ""}`);
  const topKind = topOf("kind", (k) => KIND_LABEL[k] || k);
  const topAuthor = topOf("author_name");

  const fact = (label, top) =>
    top ? `<div class="dash-fact"><span class="dash-fact-label">${esc(label)}</span><span class="dash-fact-value">${esc(top.label)} <em>(${top.n} ${esc(scope)})</em></span></div>` : "";
  facts.innerHTML =
    fact("Activité la plus partagée", topSport) +
    fact("Niveau le plus partagé", topNiveau) +
    fact("Type le plus partagé", topKind) +
    fact("Contributeur le plus actif", topAuthor);
}

/* ------------------- Bibliothèque commune : modération --------------------
   Les propositions arrivent en `pending` (la RLS ne les montre qu'à l'admin
   et à leur auteur). Publier -> `approved` (visible de tous) ; Écarter ->
   `rejected` (conservée pour trace, invisible). */
const NIVEAU_LABEL = { 1: "découverte", 2: "apprentissage", 3: "consolidation", 4: "approfondissement", 5: "expertise" };
async function renderCommunityModeration() {
  const box = $("dashCommunity");
  if (!box) return;
  const { data, error } = await supabase
    .from("community_schemas")
    .select("id,title,kind,sport,niveau,author_name,data,created_at")
    .eq("status", "pending")
    .order("created_at", { ascending: true });
  if (error) { box.innerHTML = `<p class="dash-help">Modération indisponible : ${esc(error.message)}</p>`; return; }
  const rows = data || [];
  if (!rows.length) { box.innerHTML = `<p class="dash-help">✅ Rien à valider — toutes les propositions ont été traitées.</p>`; return; }
  box.innerHTML = "";
  rows.forEach((r) => {
    const item = document.createElement("div");
    item.className = "mod-item";
    const cv = document.createElement("canvas");
    cv.width = 300; cv.height = 210; cv.className = "mod-thumb";
    try { renderToCanvas(cv, r.data, r.sport, r.data?.meta?.renderMode || "color", { compact: true }); } catch (_) { /* vignette non bloquante */ }
    const info = document.createElement("div");
    info.className = "mod-info";
    info.innerHTML = `<strong>${esc(r.title || "Sans titre")}</strong>
      <span>${esc(sportLabel(r.sport))} · ${r.kind === "exercise" ? "Exercice" : "Match"}${r.niveau ? ` · Niveau ${r.niveau} (${esc(NIVEAU_LABEL[r.niveau] || "")})` : ""}</span>
      <span>par <strong>${esc(r.author_name || "?")}</strong> · ${fmtDay(r.created_at)}</span>`;
    const actions = document.createElement("div");
    actions.className = "mod-actions";
    // EXAMINER : ouvre la proposition dans l'éditeur (schéma complet + fiche
    // pédagogique) — en copie, l'original en attente n'est pas modifié.
    const review = document.createElement("button");
    review.textContent = "👁 Examiner";
    review.title = "Ouvrir le schéma et sa fiche dans l'éditeur avant de décider";
    review.addEventListener("click", () => openForReview && openForReview(r));
    cv.style.cursor = "pointer";
    cv.title = "Ouvrir le schéma et sa fiche dans l'éditeur";
    cv.addEventListener("click", () => openForReview && openForReview(r));
    const approve = document.createElement("button");
    approve.className = "primary"; approve.textContent = "✅ Publier";
    const reject = document.createElement("button");
    reject.textContent = "❌ Écarter";
    const decide = async (status) => {
      approve.disabled = reject.disabled = true;
      const { error: e2 } = await supabase.from("community_schemas").update({ status }).eq("id", r.id);
      if (e2) { approve.disabled = reject.disabled = false; info.insertAdjacentHTML("beforeend", `<span class="mod-err">Échec : ${esc(e2.message || e2)}</span>`); return; }
      item.remove();
      onModerated && onModerated();
      if (!box.querySelector(".mod-item")) renderCommunityModeration();
    };
    approve.addEventListener("click", () => decide("approved"));
    reject.addEventListener("click", () => decide("rejected"));
    actions.append(review, approve, reject);
    item.append(cv, info, actions);
    box.appendChild(item);
  });
}

/* ---------------------------- Indicateurs clés ---------------------------- */
function renderKpis(d) {
  const kpi = (label, value, hint = "") =>
    `<div class="kpi"><span class="kpi-value">${esc(value)}</span><span class="kpi-label">${label}</span>${hint ? `<span class="kpi-hint">${hint}</span>` : ""}</div>`;
  $("dashKpis").innerHTML =
    kpi("Comptes inscrits", d.users_total ?? 0) +
    kpi("Actifs ce mois", d.active_month ?? 0, "≥ 1 génération") +
    kpi("Générations ce mois", d.gens_month ?? 0) +
    kpi("Générations au total", d.gens_total ?? 0) +
    kpi("Schémas stockés", d.schemas_total ?? 0) +
    kpi("Essais sans compte", d.trials_total ?? 0);
}

/* ------------------- Graphique barres : 30 derniers jours ------------------ */
function renderChart(byDay) {
  const counts = new Map(byDay.map((r) => [r.day, r.n]));
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const dt = new Date(Date.now() - i * 86400000);
    const key = dt.toISOString().slice(0, 10);
    days.push({ key, n: counts.get(key) || 0 });
  }
  const max = Math.max(1, ...days.map((d) => d.n));
  $("dashChart").innerHTML = days.map((d) =>
    `<div class="bar" title="${fmtDay(d.key)} : ${d.n} génération${d.n > 1 ? "s" : ""}">` +
    `<span class="bar-fill" style="height:${Math.round((d.n / max) * 100)}%"></span></div>`
  ).join("");
}

/* --------------------- Barres horizontales (répartitions) ------------------ */
function renderBars(el, rows) {
  if (!rows.length) { el.innerHTML = `<p class="dash-empty">Aucune donnée pour l'instant.</p>`; return; }
  const max = Math.max(1, ...rows.map(([, n]) => n));
  el.innerHTML = rows.slice(0, 12).map(([label, n]) =>
    `<div class="hbar"><span class="hbar-label">${esc(label)}</span>` +
    `<span class="hbar-track"><span class="hbar-fill" style="width:${Math.round((n / max) * 100)}%"></span></span>` +
    `<span class="hbar-n">${n}</span></div>`
  ).join("");
}

/* ------------------------------- Utilisateurs ------------------------------ */
function renderUsers(users) {
  const rows = users.map((u) => `
    <tr>
      <td>${esc(u.email || "—")}</td>
      <td><span class="plan-pill plan-${esc(u.plan)}">${esc(u.plan)}</span></td>
      <td class="num">${u.used}</td>
      <td class="num">${u.schemas}</td>
      <td>${fmtDay(u.created_at)}</td>
    </tr>`).join("");
  $("dashUsers").innerHTML =
    `<thead><tr><th>E-mail</th><th>Plan</th><th>Gén. ce mois</th><th>Schémas</th><th>Inscrit le</th></tr></thead>` +
    `<tbody>${rows || `<tr><td colspan="5" class="dash-empty">Aucun compte.</td></tr>`}</tbody>`;
}

/* ============================================================================
   GESTION DES PUBLICITÉS
   ========================================================================== */
async function renderAdsAdmin() {
  const box = $("adsList");
  const { data, error } = await supabase.from("ads").select("*").order("position", { ascending: true });
  if (error) { box.innerHTML = `<p class="dash-empty">Chargement des annonces impossible (${esc(error.message)}).</p>`; return; }
  if (!data.length) { box.innerHTML = `<p class="dash-empty">Aucune annonce. Le carrousel est masqué tant qu'aucune annonce n'est active.</p>`; return; }

  // Synthèse d'audience (clics / impressions / CTR moyen).
  const totC = data.reduce((s, a) => s + (a.clicks || 0), 0);
  const totI = data.reduce((s, a) => s + (a.impressions || 0), 0);
  const ctr = (c, i) => (i > 0 ? ((c / i) * 100).toFixed(1) + " %" : "—");

  box.innerHTML = `<p class="ads-metrics">📊 Audience cumulée : <strong>${totC}</strong> clic${totC > 1 ? "s" : ""} · <strong>${totI}</strong> impression${totI > 1 ? "s" : ""} · CTR moyen <strong>${ctr(totC, totI)}</strong></p>`;
  data.forEach((ad) => {
    const row = document.createElement("div");
    row.className = "ad-row" + (ad.active ? "" : " is-off");
    row.innerHTML = `
      <img class="ad-row-thumb" src="${esc(ad.image_url)}" alt="">
      <div class="ad-row-info">
        <a href="${esc(ad.href)}" target="_blank" rel="noopener">${esc(ad.href)}</a>
        <span>${esc(ad.alt || "(sans description)")}</span>
        <span class="ad-row-stats">👁️ ${ad.impressions || 0} · 👆 ${ad.clicks || 0} · CTR ${ctr(ad.clicks || 0, ad.impressions || 0)}</span>
      </div>
      <label class="ad-row-toggle"><input type="checkbox" ${ad.active ? "checked" : ""}> Active</label>
      <button class="iconbtn ad-row-del" title="Supprimer l'annonce">🗑️</button>`;
    row.querySelector("input[type=checkbox]").addEventListener("change", async (e) => {
      const { error: err } = await supabase.from("ads").update({ active: e.target.checked }).eq("id", ad.id);
      if (err) { alert("Mise à jour impossible : " + err.message); e.target.checked = !e.target.checked; return; }
      row.classList.toggle("is-off", !e.target.checked);
      onAdsChanged && onAdsChanged();
    });
    row.querySelector(".ad-row-del").addEventListener("click", async () => {
      if (!confirm("Supprimer cette annonce ?")) return;
      const { error: err } = await supabase.from("ads").delete().eq("id", ad.id);
      if (err) { alert("Suppression impossible : " + err.message); return; }
      row.remove();
      onAdsChanged && onAdsChanged();
    });
    box.appendChild(row);
  });
}

function adFormError(msg) { const e = $("adError"); e.textContent = msg || ""; e.classList.toggle("active", !!msg); }

function bindAdsForm() {
  if (bound) return;
  bound = true;
  $("adAddBtn").addEventListener("click", async () => {
    adFormError("");
    const file = $("adFile").files[0] || null;
    let imageUrl = $("adImgUrl").value.trim();
    const href = $("adHref").value.trim();
    const alt = $("adAlt").value.trim();
    if (!file && !imageUrl) return adFormError("Choisis un fichier image OU colle l'URL d'une image.");
    if (!href) return adFormError("Renseigne le lien ouvert au clic.");

    $("adAddBtn").disabled = true;
    try {
      // Visuel : téléversé dans le bucket public « ads » (URL publique stable).
      if (file) {
        if (file.size > 300 * 1024) throw new Error("Image trop lourde (max 300 Ko) — vise 728×90 px, < 200 Ko.");
        const ext = (file.name.split(".").pop() || "png").toLowerCase();
        const path = `ad-${Date.now()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("ads").upload(path, file, { cacheControl: "604800" });
        if (upErr) throw upErr;
        imageUrl = supabase.storage.from("ads").getPublicUrl(path).data.publicUrl;
      }
      const { count } = await supabase.from("ads").select("id", { count: "exact", head: true });
      const { error: insErr } = await supabase.from("ads").insert({ image_url: imageUrl, href, alt, position: count || 0 });
      if (insErr) throw insErr;
      $("adFile").value = ""; $("adImgUrl").value = ""; $("adHref").value = ""; $("adAlt").value = "";
      await renderAdsAdmin();
      onAdsChanged && onAdsChanged();
    } catch (err) {
      adFormError("Ajout impossible : " + (err?.message || err));
    } finally {
      $("adAddBtn").disabled = false;
    }
  });
  $("dashRefresh").addEventListener("click", () => renderDashboard());
}
